/**
 * Розрахунок рядка з переповненням (components.md, розд. 12; stability.md, розд. 1, правило 11): чиста
 * функція без DOM, одна для вкладок картки, чипів, тегів, дій панелі й нижньої навігації. Що з неї
 * малювати і як міряти — `useOverflowRow.ts`.
 *
 * Правила рядка:
 * - рядок не переноситься: що не вміщується, іде в «Ще · N», останні — першими (або за `priority`);
 * - закріплені (`pinned`: основна дія, «Стрічка») лишаються в рядку завжди;
 * - вибраний (`selected`) за межею стає в рядок останнім перед закріпленими в кінці, витіснений іде
 *   в «Ще» (рішення власника 2026-10-27, layout.md, розд. 7, правило 2);
 * - ширина `0` — елемент сховав CSS (вузька «Деталі» на широкій області): місця не займає.
 */

export interface RowGeometry {
  /** Місце під елементи рядка (без «Ще» і без сталих сусідів), px. */
  width: number;
  /** Ширина кнопки «Ще» разом з відступом до рядка. */
  more: number;
  /** Проміжок між елементами. */
  gap: number;
}

export interface RowFit extends RowGeometry {
  /** Індекси, що лишаються в рядку завжди. */
  pinned?: ReadonlySet<number>;
  /** Індекс вибраного; `-1` — немає. */
  selected?: number;
  /** «Ще» потрібна й без надлишку (у меню є власні пункти): місце під неї резервується завжди. */
  always?: boolean;
  /**
   * Порядок, у якому елементи займають місце (перші — найважливіші). Немає — порядок рядка, тобто
   * в «Ще» першими йдуть останні. Елементи поза переліком — після нього, у порядку рядка.
   */
  priority?: readonly number[];
}

/** Індекси елементів у рядку в порядку рядка. Решта — у «Ще». */
export function fitRow(widths: readonly number[], fit: RowFit): number[] {
  const { width, more, gap, pinned = new Set<number>(), selected = -1, always = false } = fit;
  const all = widths.map((_, i) => i);
  const shown = widths.filter((w) => w > 0);
  const total = shown.reduce((sum, w) => sum + w, 0) + Math.max(0, shown.length - 1) * gap;
  if (total <= width - (always ? more + gap : 0)) return all;
  const cost = (i: number) => {
    const w = widths[i] ?? 0;
    return w > 0 ? w + gap : 0;
  };
  const listed = new Set(fit.priority ?? []);
  const order = fit.priority ? [...fit.priority, ...all.filter((i) => !listed.has(i))] : all;
  const room = width - more;
  const fixed = all.filter((i) => pinned.has(i)).reduce((sum, i) => sum + cost(i), 0);
  // Перший, що не вмістився, зупиняє: менші за ним не «пролазять» у рядок поза порядком.
  const prefix = (budget: number, skip: number) => {
    const out = new Set<number>();
    let used = fixed;
    for (const i of order) {
      if (pinned.has(i) || i === skip || i < 0 || i >= widths.length) continue;
      used += cost(i);
      if (used > budget) break;
      out.add(i);
    }
    return out;
  };
  const natural = prefix(room, -1);
  const keep = selected >= 0 && !pinned.has(selected) && !natural.has(selected);
  const chosen = keep ? prefix(room - cost(selected), selected) : natural;
  const row = all.filter((i) => pinned.has(i) || chosen.has(i));
  if (!keep) return row;
  // Вибраний — після останнього незакріпленого, перед закріпленими в кінці рядка.
  const lastFree = row.findLastIndex((i) => !pinned.has(i));
  const at = lastFree >= 0 ? lastFree + 1 : row.findLastIndex((i) => i < selected) + 1;
  return [...row.slice(0, at), selected, ...row.slice(at)];
}
